import {
  CalendarDate,
  CalendarDateTime,
  parseAbsoluteToLocal,
  parseDate,
  toCalendarDateTime,
} from '@internationalized/date';
import type { DateValue } from 'react-aria-components';

export function toCalendarDate(value: string | null | undefined): CalendarDate | null;
export function toCalendarDate(value: DateValue): CalendarDate;
export function toCalendarDate(value: string | null | undefined | DateValue): CalendarDate | null {
  if (!value) return null;
  if (typeof value === 'string') {
    try {
      return parseDate(value.split('T')[0]);
    } catch {
      return null;
    }
  }
  // Extract the date portion from any DateValue (CalendarDate, CalendarDateTime, etc.)
  return new CalendarDate(value.year, value.month, value.day);
}

export function fromCalendarDate(value: DateValue | null): string | null {
  if (!value) return null;
  // Two-step construction: start with year 2000 as a placeholder, then overwrite with setUTCFullYear.
  // Date.UTC() treats two-digit years (0–99) as 1900+year (a legacy JS quirk), so passing the real
  // year directly would corrupt dates like 2025. setUTCFullYear has no such special-casing.
  const date = new Date(Date.UTC(2000, value.month - 1, value.day, 0, 0, 0, 0));
  date.setUTCFullYear(value.year);
  return date.toISOString();
}

export function parseDateTime(value: string | null | undefined): CalendarDateTime | null {
  if (!value) return null;
  try {
    return toCalendarDateTime(parseAbsoluteToLocal(value));
  } catch {
    return null;
  }
}

export function fromDateValue(value: DateValue | null): string | null {
  if (!value) return null;
  if ('hour' in value) {
    const dt = value as CalendarDateTime;
    // CalendarDateTime hours are local time (from parseAbsoluteToLocal),
    // so use local Date constructor — toISOString() converts to UTC automatically.
    // Two-step year assignment: setFullYear has no year 0–99 special-casing, unlike the Date constructor.
    const date = new Date(2000, dt.month - 1, dt.day, dt.hour, dt.minute, dt.second || 0);
    date.setFullYear(dt.year);
    return date.toISOString();
  }
  return fromCalendarDate(value);
}
